import { useEffect, useRef } from "react";
import { useReducedMotion } from "motion/react";

/**
 * Living hero backdrop: a loose grid of energy "nodes" wired into a network,
 * with light pulses (流光) streaming along the edges and softly glowing nodes.
 * One canvas, capped DPR, glow stamped from pre-baked sprites → cheap to animate.
 * Honors prefers-reduced-motion by painting a single static frame.
 */
const BRAND = "1,120,254"; // #0178fe
const CYAN = "0,200,247"; // #00c8f7

type Node = { bx: number; by: number; x: number; y: number; depth: number; phase: number };
type Edge = { a: number; b: number };
type Pulse = { edge: number; t: number; speed: number };

function makeGlow(color: string, size = 96) {
  const c = document.createElement("canvas");
  c.width = c.height = size;
  const g = c.getContext("2d")!;
  const grd = g.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2);
  grd.addColorStop(0, `rgba(${color},1)`);
  grd.addColorStop(0.5, `rgba(${color},0.35)`);
  grd.addColorStop(1, `rgba(${color},0)`);
  g.fillStyle = grd;
  g.fillRect(0, 0, size, size);
  return c;
}

export function HeroBackground({ className = "" }: { className?: string }) {
  const ref = useRef<HTMLCanvasElement>(null);
  const reduced = useReducedMotion();

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const brandGlow = makeGlow(BRAND);
    const cyanGlow = makeGlow(CYAN);

    let w = 0;
    let h = 0;
    let raf = 0;
    let nodes: Node[] = [];
    let edges: Edge[] = [];
    let pulses: Pulse[] = [];
    const ptr = { x: 0.5, y: 0.5, tx: 0.5, ty: 0.5 };

    const build = () => {
      const cols = Math.min(16, Math.max(5, Math.round(w / 150)));
      const rows = Math.min(10, Math.max(4, Math.round(h / 150)));
      const cw = w / cols;
      const ch = h / rows;
      nodes = [];
      for (let r = 0; r <= rows; r++) {
        for (let c = 0; c <= cols; c++) {
          const x = c * cw + (Math.random() - 0.5) * cw * 0.55;
          const y = r * ch + (Math.random() - 0.5) * ch * 0.55;
          nodes.push({ bx: x, by: y, x, y, depth: 0.4 + Math.random() * 0.8, phase: Math.random() * Math.PI * 2 });
        }
      }
      edges = [];
      const reach = Math.max(cw, ch) * 1.35;
      for (let i = 0; i < nodes.length; i++) {
        for (let j = i + 1; j < nodes.length; j++) {
          const dx = nodes[i]!.bx - nodes[j]!.bx;
          const dy = nodes[i]!.by - nodes[j]!.by;
          if (dx * dx + dy * dy < reach * reach) edges.push({ a: i, b: j });
        }
      }
      pulses = [];
      const count = Math.min(46, Math.round(edges.length * 0.07));
      for (let k = 0; k < count; k++) {
        pulses.push({ edge: Math.floor(Math.random() * edges.length), t: Math.random(), speed: 0.06 + Math.random() * 0.12 });
      }
    };

    const resize = () => {
      const rect = canvas.getBoundingClientRect();
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      w = rect.width;
      h = rect.height;
      canvas.width = Math.max(1, Math.round(w * dpr));
      canvas.height = Math.max(1, Math.round(h * dpr));
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      build();
    };

    let prev = performance.now();
    const draw = (now: number) => {
      const dt = Math.min(0.05, (now - prev) / 1000);
      prev = now;
      ctx.clearRect(0, 0, w, h);

      ptr.x += (ptr.tx - ptr.x) * 0.06;
      ptr.y += (ptr.ty - ptr.y) * 0.06;
      const px = (ptr.x - 0.5) * 36;
      const py = (ptr.y - 0.5) * 36;

      for (const n of nodes) {
        n.phase += dt * 0.5;
        n.x = n.bx + Math.sin(n.phase) * 7 + px * n.depth;
        n.y = n.by + Math.cos(n.phase * 0.8) * 7 + py * n.depth;
      }

      ctx.lineWidth = 1;
      for (const e of edges) {
        const a = nodes[e.a]!;
        const b = nodes[e.b]!;
        ctx.strokeStyle = `rgba(${BRAND},0.12)`;
        ctx.beginPath();
        ctx.moveTo(a.x, a.y);
        ctx.lineTo(b.x, b.y);
        ctx.stroke();
      }

      // glowing nodes
      ctx.globalCompositeOperation = "lighter";
      for (const n of nodes) {
        const pulse = 0.5 + 0.5 * Math.sin(n.phase * 2);
        const s = 10 + pulse * 10;
        ctx.globalAlpha = 0.12 + pulse * 0.22;
        ctx.drawImage(brandGlow, n.x - s, n.y - s, s * 2, s * 2);
      }
      ctx.globalAlpha = 1;
      for (const n of nodes) {
        ctx.fillStyle = "rgba(210,235,255,0.85)";
        ctx.beginPath();
        ctx.arc(n.x, n.y, 1.3, 0, Math.PI * 2);
        ctx.fill();
      }

      // streaming pulses (流光)
      for (const p of pulses) {
        if (!reduced) p.t += p.speed * dt;
        if (p.t > 1) {
          p.t = 0;
          p.edge = Math.floor(Math.random() * edges.length);
        }
        const e = edges[p.edge];
        if (!e) continue;
        const a = nodes[e.a]!;
        const b = nodes[e.b]!;
        const x = a.x + (b.x - a.x) * p.t;
        const y = a.y + (b.y - a.y) * p.t;
        const s = 9;
        ctx.globalAlpha = Math.sin(p.t * Math.PI); // fade in/out along the edge
        ctx.drawImage(cyanGlow, x - s, y - s, s * 2, s * 2);
      }
      ctx.globalAlpha = 1;
      ctx.globalCompositeOperation = "source-over";

      raf = requestAnimationFrame(draw);
    };

    const onMove = (e: PointerEvent) => {
      ptr.tx = e.clientX / window.innerWidth;
      ptr.ty = e.clientY / window.innerHeight;
    };

    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(canvas);

    if (reduced) {
      draw(performance.now());
      cancelAnimationFrame(raf);
    } else {
      window.addEventListener("pointermove", onMove, { passive: true });
      raf = requestAnimationFrame(draw);
    }

    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      window.removeEventListener("pointermove", onMove);
    };
  }, [reduced]);

  return <canvas ref={ref} aria-hidden className={`pointer-events-none ${className}`} />;
}
